import { useQuery } from '@tanstack/react-query'
import { Link, useCanGoBack, useParams, useRouter } from '@tanstack/react-router'
import { ArrowLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { AsortiGrid } from '@app/components/asorti-grid'
import { CariLink, ModelLink, OrderLink, StockLink } from '@app/components/entity-link'
import { ProductImage } from '@app/components/product-image'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { productionApi, productionKeys } from '@app/features/uretim/api'
import {
  toAsortiCells,
  STATUS_HINT,
  statusTone,
  delayLabel,
  plannedDelayLabel,
  formatDecimal,
  formatNumber,
  formatDate,
  formatDateTime,
} from '@app/features/uretim/format'
import { EmptyState, ErrorState, Progress, Skeleton, Stat } from '@app/features/uretim/parts'
import type {
  StageKind,
  EmirStage,
  EmirHeader,
  EmirDetailResponse,
  MaterialProsesGroup,
} from '@app/features/uretim/production-api'
import { ApiError } from '@app/lib/api'
import { cn } from '@app/lib/cn'

/**
 * Emir detail — the shop floor's work ticket.
 *
 * The backbone is the route strip: the model's real proses sequence
 * (`Model_P.ProsesNo`, not the proses code's own order) runs left to right,
 * and the step the batch is currently sitting at is marked in kordovan. That
 * is the screen's one accented element; everything else stays quiet.
 */
export function EmirDetailScreen() {
  const { emirNo } = useParams({ from: '/_korumali/uretim/$emirNo' })
  const number = Number.parseInt(emirNo, 10)

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: productionKeys.emir(number),
    queryFn: () => productionApi.emir(number),
    enabled: Number.isSafeInteger(number) && number > 0,
  })

  if (!Number.isSafeInteger(number) || number <= 0) {
    return (
      <>
        <BackLink />
        <Card>
          <EmptyState
            title="Geçersiz emir numarası"
            description="Adresteki emir numarası okunamadı. Üretim listesinden bir emir seçin."
            action={
              <Button asChild variant="primary" size="sm">
                <Link to="/uretim">Üretim listesine dön</Link>
              </Button>
            }
          />
        </Card>
      </>
    )
  }

  if (isError) {
    // The status code, not the message text: matching on "bulunamadı" turned a
    // 500 whose message happened to contain the word into a "no such emir" card.
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <>
        <BackLink />
        <Card>
          {notFound ? (
            <EmptyState
              title="Üretim emri bulunamadı"
              description={`${number} numaralı emir kayıtlı değil. Listeden doğru emri seçebilirsiniz.`}
              action={
                <Button asChild variant="primary" size="sm">
                  <Link to="/uretim">Üretim listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState
              message={error instanceof Error ? error.message : undefined}
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      </>
    )
  }

  if (isPending) {
    return (
      <>
        <BackLink />
        <DetailSkeleton />
      </>
    )
  }

  return <EmirDetail detail={data} />
}

const BACK_LINK_CLASS =
  'mb-2 inline-flex items-center gap-1.5 text-data text-ink-muted transition-colors hover:text-kordovan'

/**
 * History back when there is in-app history: the list keeps its filters, page
 * and scroll in the URL, and only the history entry still has them — a plain
 * `<Link to="/uretim">` pushed the default list and threw all of it away. The
 * plain link stays as the fallback for direct arrivals (deep link, new tab).
 *
 * The label assumes the previous entry IS the emir list, which holds today
 * because only the board and the list link into a detail. If another screen
 * ever deep-links here, revisit — "Üretim emirleri" would then lie about
 * where back leads.
 */
function BackLink() {
  const router = useRouter()
  const canGoBack = useCanGoBack()

  if (canGoBack) {
    return (
      <button type="button" onClick={() => router.history.back()} className={BACK_LINK_CLASS}>
        <ArrowLeftIcon className="size-3.5" aria-hidden />
        Üretim emirleri
      </button>
    )
  }
  return (
    <Link to="/uretim" className={BACK_LINK_CLASS}>
      <ArrowLeftIcon className="size-3.5" aria-hidden />
      Üretim emirleri
    </Link>
  )
}

function EmirDetail({ detail }: { detail: EmirDetailResponse }) {
  const { header, plan, stages, materials } = detail

  return (
    <>
      <BackLink />

      <ScreenHeader
        title={`Emir ${header.emirNo}`}
        description={
          header.model
            ? `${header.model}${header.color ? ` · ${header.color}` : ''}`
            : (header.modelKod ?? undefined)
        }
        actions={
          <Badge
            tone={statusTone(header.status)}
            title={header.status === '' ? STATUS_HINT.planned : undefined}
          >
            {header.statusLabel}
          </Badge>
        }
      />

      <div className="flex flex-col gap-4">
        <Summary header={header} plan={plan} />
        {/* Keyed so the once-per-emir centering resets when the emir changes. */}
        <RouteStrip key={header.emirNo} stages={stages} />
        <SizeDistribution stages={stages} />
        <MaterialTable groups={materials} totalPairs={header.totalPairs} />
      </div>
    </>
  )
}

/* ── Summary ────────────────────────────────────────────────────────────────── */

function Summary({ header, plan }: { header: EmirHeader; plan: EmirDetailResponse['plan'] }) {
  const planned = header.status === ''
  // Red only for production actually running late: a planned emir was never
  // released, so its past termin is a stale plan, not a floor holdup.
  const overdue = header.status === 'B' && (header.delayDays ?? 0) > 0
  // "How many days overdue" is misleading on a closed emir: we don't know the shipping date.
  const delay =
    header.status === 'X'
      ? null
      : planned
        ? plannedDelayLabel(header.delayDays)
        : delayLabel(header.delayDays)

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        {/*
          Every code on this ticket opens the record behind it: the model card
          holds the route and the recipe, the stock card the on-hand, the order
          the ship date the shop floor is racing, the cari who it is for. In
          Korgun each of those is a separate module the user re-searches by hand.
        */}
        <div className="flex items-start gap-4">
          {/*
           * What the batch on the floor should look like, next to the numbers
           * that describe it — the emir's own colourway photo, since an emir is
           * one SKod × one RKod. Dropped below `sm`, where the field grid is
           * already a single column.
           */}
          <ProductImage
            imageKey={header.imageKey}
            alt={`${header.model ?? header.modelKod ?? 'Model'}${header.color ? ` · ${header.color}` : ''} fotoğrafı`}
            size="hero"
            className="hidden w-32 sm:block"
          />

          <div className="grid min-w-0 flex-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Model"
              value={header.model ?? header.modelKod}
              sub={header.modelKod ? <ModelLink modelKod={header.modelKod} /> : null}
              mono
            />
            <Field
              label="Renk"
              value={header.color}
              sub={header.rkod === null ? null : String(header.rkod)}
            />
            <Field
              label="Müşteri"
              value={
                header.cariKod && header.customer ? (
                  <CariLink
                    ckod={header.cariKod}
                    title={`${header.customer} cari kartını aç`}
                    className="text-ink hover:text-kordovan hover:no-underline"
                    // No hover accent when it is not a link (no cari access).
                    plainClassName="text-ink"
                  >
                    {header.customer}
                  </CariLink>
                ) : (
                  header.customer
                )
              }
              sub={
                header.sipNo === null ? (
                  header.cariKod ? (
                    <CariLink ckod={header.cariKod} />
                  ) : null
                ) : (
                  <OrderLink sipNo={header.sipNo}>Sipariş {header.sipNo}</OrderLink>
                )
              }
            />
            <Field
              label="Termin"
              value={formatDate(header.deliveryDate)}
              sub={delay}
              mono
              accent={overdue}
            />
            <Field
              label="Stok kartı"
              value={header.stockName ?? header.skod}
              sub={header.skod ? <StockLink skod={header.skod} /> : null}
              mono
            />
            <Field label="Beden grubu" value={header.sizeGroup} mono />
            <Field
              label="Lokasyon"
              value={header.locationName ?? header.location}
              sub={header.location}
            />
            <Field
              label="Emir tarihi"
              value={formatDateTime(header.date)}
              sub={header.openedBy ? `${header.openedBy} açtı` : null}
              mono
            />
          </div>
        </div>

        {header.note ? (
          <p className="rounded-xs border border-hairline bg-kagit px-2.5 py-2 text-data text-ink">
            {header.note}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-x-7 gap-y-3">
          <Stat label="Toplam" value={formatNumber(header.totalPairs)} unit="çift" />
          <Stat label="Bekleyen" value={formatNumber(header.waitingPairs)} tone="warn" />
          <Stat label="İşlemde" value={formatNumber(header.inProgressPairs)} tone="info" />
          <Stat label="Biten" value={formatNumber(header.donePairs)} tone="ok" />
          <Stat label="Üretime verilmedi" value={formatNumber(header.notStartedPairs)} />
          {header.fire > 0 ? (
            <Stat label="Fire" value={formatNumber(header.fire)} tone="accent" />
          ) : null}
        </div>

        <Progress
          total={header.totalPairs}
          done={header.donePairs}
          inProgress={header.inProgressPairs}
          waiting={header.waitingPairs}
          notStarted={header.notStartedPairs}
          className="h-2"
        />

        {plan.length > 0 ? (
          <AsortiGrid label="Emir asortisi" cells={toAsortiCells(plan)} total />
        ) : null}
      </CardContent>
    </Card>
  )
}

function Field({
  label,
  value,
  sub,
  mono = false,
  accent = false,
}: {
  label: string
  /** Plain text, or the entity link that opens the record it names. */
  value: ReactNode
  sub?: ReactNode
  mono?: boolean
  accent?: boolean
}) {
  return (
    <div className="min-w-0">
      <p className="eyebrow">{label}</p>
      <p
        className={cn(
          'truncate text-data text-ink',
          mono && 'font-mono tnum',
          accent && 'text-kordovan',
        )}
      >
        {value ?? '—'}
      </p>
      {sub ? (
        <p className={cn('truncate text-micro', accent ? 'text-kordovan' : 'text-ink-muted')}>
          {sub}
        </p>
      ) : null}
    </div>
  )
}

/* ── Route strip ───────────────────────────────────────────────────────────── */

function RouteStrip({ stages }: { stages: readonly EmirStage[] }) {
  /*
   * A 6–8 step route is wider than the content area, and the step the batch
   * is at can sit past the fold with no cue that the strip scrolls. Center
   * the active step ONCE per emir (the caller keys this component by emirNo)
   * — only the strip's own scrollLeft is touched (scrollIntoView would also
   * scroll the PAGE to the strip), and a background refetch must not yank a
   * user who has scrolled off to read another step.
   */
  const scrollerRef = useRef<HTMLDivElement>(null)
  const centered = useRef(false)
  useEffect(() => {
    if (centered.current) return
    const scroller = scrollerRef.current
    const active = scroller?.querySelector<HTMLElement>('[aria-current="step"]')
    if (!scroller || !active) return
    centered.current = true
    const delta = active.getBoundingClientRect().left - scroller.getBoundingClientRect().left
    scroller.scrollTo({
      left: scroller.scrollLeft + delta - (scroller.clientWidth - active.clientWidth) / 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }, [stages])

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Rota</CardTitle>
          <p className="mt-0.5 text-micro text-ink-muted">
            Modelin gerçek proses sırası. Kordovan işaretli adım, partinin şu an durduğu yer.
          </p>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {stages.length === 0 ? (
          <EmptyState
            title="Rota tanımlı değil"
            description="Bu emrin modelinde proses rotası ve üretim hareketi yok. Model kartından proses rotasını tanımlayın."
          />
        ) : (
          <div ref={scrollerRef} className="overflow-x-auto px-3.5 py-3">
            <ol className="flex min-w-max items-stretch gap-1">
              {stages.map((stage, index) => (
                <li key={stage.proses} className="flex items-stretch gap-1">
                  <RouteStep stage={stage} />
                  {index < stages.length - 1 ? (
                    <ChevronRightIcon
                      aria-hidden
                      className="size-3.5 shrink-0 self-center text-ink-muted/60"
                    />
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function RouteStep({ stage }: { stage: EmirStage }) {
  const hasFire = stage.fire > 0
  const worker = stage.personnel.filter((p) => p.role === 'worker')

  return (
    <article
      aria-current={stage.active ? 'step' : undefined}
      className={cn(
        'flex w-44 flex-col gap-2 rounded-xs border-l-2 px-2.5 py-2',
        stage.active
          ? 'border-l-kordovan bg-kordovan/6'
          : stage.idle
            ? 'border-l-hairline bg-kagit/60'
            : 'border-l-zeytin/50 bg-kagit',
        hasFire && 'ring-1 ring-kordovan/35',
      )}
    >
      <header className="flex items-baseline gap-1.5">
        <span className="font-mono text-micro text-ink-muted tnum">{stage.proses}</span>
        <h3
          className={cn(
            'truncate font-display text-data font-semibold tracking-tight',
            stage.active ? 'text-kordovan' : 'text-ink',
          )}
        >
          {stage.prosesName}
        </h3>
      </header>

      {stage.idle ? (
        <p className="text-micro text-ink-muted">Henüz hareket yok</p>
      ) : (
        <dl className="flex flex-col gap-px">
          <StepRow label="Bekleyen" value={stage.waitingPairs} color="text-hardal" />
          <StepRow label="İşlemde" value={stage.inProgressPairs} color="text-celik" />
          <StepRow
            label="Geçen"
            title="Bu prosesi bitirip sonrakine geçen çift"
            value={stage.passedPairs}
            color="text-ink-muted"
          />
          {stage.donePairs > 0 ? (
            <StepRow label="Mamul" value={stage.donePairs} color="text-zeytin" />
          ) : null}
          {hasFire ? <StepRow label="Fire" value={stage.fire} color="text-kordovan" /> : null}
        </dl>
      )}

      {worker.length > 0 ? (
        <p className="truncate text-micro text-ink-muted" title={worker.map((p) => p.name).join(', ')}>
          {worker.map((p) => p.name).join(', ')}
        </p>
      ) : null}

      {stage.machines.length > 0 ? (
        <p className="truncate font-mono text-micro text-ink-muted tnum">
          {stage.machines.map((m) => m.wmKod ?? `#${m.wMakNum}`).join(', ')}
        </p>
      ) : null}

      {stage.firstStart ? (
        <p className="text-micro text-ink-muted">
          {formatDate(stage.firstStart)}
          {stage.lastEnd ? ` → ${formatDate(stage.lastEnd)}` : ' →'}
        </p>
      ) : null}
    </article>
  )
}

function StepRow({
  label,
  value,
  color,
  title,
}: {
  label: string
  value: number
  color: string
  /** Hover explanation for labels whose Turkish is ambiguous ("Geçen"). */
  title?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt title={title} className="text-micro text-ink-muted">
        {label}
      </dt>
      <dd className={cn('font-mono text-data tnum', value === 0 ? 'text-ink-muted/45' : color)}>
        {value === 0 ? '·' : formatNumber(value)}
      </dd>
    </div>
  )
}

/* ── Size distribution ────────────────────────────────────────────────────────── */

const SERIES_LABEL: Record<StageKind, string> = {
  waiting: 'Bekleyen',
  inProgress: 'İşlemde',
  passed: 'Geçen',
  done: 'Mamul',
}

function SizeDistribution({ stages }: { stages: readonly EmirStage[] }) {
  const rows = stages
    .map((stage) => {
      // On the terminal proses "passed" and "done" are the same count; we don't draw both.
      const kinds: StageKind[] = ['waiting', 'inProgress', 'passed', 'done']
      const filled = kinds.filter((kind) => {
        if (stage.sizes[kind].length === 0) return false
        if (kind === 'passed' && stage.sizes.done.length > 0) return false
        return true
      })
      return { stage, kinds: filled }
    })
    .filter((row) => row.kinds.length > 0)

  if (rows.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Prosese göre beden dağılımı</CardTitle>
          <p className="mt-0.5 text-micro text-ink-muted">
            Hangi bedenin hangi proseste kaldığı — asorti eksiği burada görünür.
          </p>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-0">
        {rows.map(({ stage, kinds }) => (
          <div
            key={stage.proses}
            className="flex flex-wrap items-start gap-x-6 gap-y-3 border-b border-hairline px-3.5 py-3 last:border-b-0"
          >
            <div className="w-32 shrink-0">
              <p className="font-mono text-micro text-ink-muted tnum">{stage.proses}</p>
              <p
                className={cn(
                  'font-display text-data font-semibold tracking-tight',
                  stage.active ? 'text-kordovan' : 'text-ink',
                )}
              >
                {stage.prosesName}
              </p>
            </div>
            {kinds.map((kind) => (
              <AsortiGrid
                key={kind}
                label={SERIES_LABEL[kind]}
                cells={toAsortiCells(stage.sizes[kind])}
                density="sm"
                total
              />
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/* ── Material ──────────────────────────────────────────────────────────────── */

function MaterialTable({
  groups,
  totalPairs,
}: {
  groups: readonly MaterialProsesGroup[]
  totalPairs: number
}) {
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Malzeme</CardTitle>
          <p className="mt-0.5 text-micro text-ink-muted">
            Reçeteden patlatılan sarfiyat. Toplam = çift başına miktar ×{' '}
            <span className="font-mono tnum">{formatNumber(totalPairs)}</span> çift.
          </p>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {groups.length === 0 ? (
          <EmptyState
            title="Malzeme satırı yok"
            description="Bu emir için reçete patlatılmamış. Model kartındaki proses–hammadde satırlarını tanımlayıp emri yeniden oluşturun."
          />
        ) : (
          <TableScroller>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 text-right">Parça</TableHead>
                  {/* Wider than the code needs: the material's photo leads the cell. */}
                  <TableHead className="w-44">Stok kodu</TableHead>
                  <TableHead className="min-w-52">Tanım</TableHead>
                  <TableHead className="w-32">Renk</TableHead>
                  <TableHead className="w-24 text-right">Çift başına</TableHead>
                  <TableHead className="w-28 text-right">Toplam</TableHead>
                  <TableHead className="w-16">Birim</TableHead>
                  <TableHead className="w-20">Depo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <MaterialGroup key={group.proses} group={group} />
                ))}
              </TableBody>
            </Table>
          </TableScroller>
        )}
      </CardContent>
    </Card>
  )
}

function MaterialGroup({ group }: { group: MaterialProsesGroup }) {
  return (
    <>
      <TableRow className="bg-kagit hover:bg-kagit">
        <TableCell colSpan={8} className="h-7">
          <span className="font-mono text-micro text-ink-muted tnum">{group.proses}</span>{' '}
          <span className="font-display text-micro font-semibold uppercase tracking-[0.06em] text-ink">
            {group.prosesName}
          </span>
        </TableCell>
      </TableRow>
      {group.rows.map((row) => (
        <TableRow key={`${row.proses}-${row.partIndex}-${row.hskod}-${row.hrkod}`}>
          <TableNumCell className="text-ink-muted">{row.partIndex}</TableNumCell>
          <TableCell className="font-mono tnum">
            <span className="flex items-center gap-1.5">
              {/* Same line thumb as the model card's reçete — mostly the sheet
                  glyph, since materials are rarely photographed. */}
              <ProductImage
                imageKey={row.imageKey}
                alt={`${row.materialName ?? row.hskod} fotoğrafı`}
                kind="material"
                size="line"
              />
              <StockLink skod={row.hskod} />
            </span>
          </TableCell>
          <TableCell className="truncate">{row.materialName ?? row.hskod}</TableCell>
          <TableCell className="truncate text-ink-muted">{row.color ?? '—'}</TableCell>
          <TableNumCell className="text-ink-muted">{formatDecimal(row.unitQuantity)}</TableNumCell>
          <TableNumCell className="font-medium">{formatDecimal(row.totalQuantity)}</TableNumCell>
          <TableCell className="text-micro text-ink-muted">{row.unit ?? '—'}</TableCell>
          <TableCell className="font-mono text-micro text-ink-muted tnum">
            {row.hLocation ?? '—'}
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

/* ── Skeleton ──────────────────────────────────────────────────────────────── */

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-8 w-56" />
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
          <Skeleton className="h-2 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex gap-1 overflow-hidden">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-28 w-44 shrink-0" />
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
